<template>
  <o-panel-link :path="`/appointment/${reservation.idReservation}?date=${reservation.date}`">
    <o-card
      class="rounded-lg overflow-hidden relative mb-4 py-3 px-3 bg-gray-50 border border-gray-200 dark:bg-gray-800/20 dark:border-gray-800"
      baseClass="rounded-lg p-2 text-sm"
      bodyClass="p-0"
      actionable
    >
      <div class="flex justify-between items-center">
        <div class="flex items-center">
          <span v-if="single && reservation.customer" class="font-semibold">{{ reservation.customer.displayName }}</span>
          <span v-else-if="reservation.attendees" class="font-semibold">{{ $t('components.dashboard.elements.attendees', { count: reservation.attendees.count }) }}</span>
        </div>

        <div class="flex items-center">
          <status v-if="!reservation.groupCustomers" :reservation="reservation" size="xs" compact />
          <div v-if="timeLabel" class="bg-primary-200 text-primary-700 dark:text-white/80 dark:bg-primary-600/20 rounded-full px-2 py-1 ml-3 flex items-center h-6 mb-1">
            <span class="inline-flex h-3 w-3 mr-2 relative">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 dark:bg-primary-600/20 opacity-75" />
              <span class="relative inline-flex rounded-full h-3 w-3 bg-primary-500 dark:bg-primary-600" />
            </span>
            <span class="leading-none pt-px text-xs">{{ timeLabel }}</span>
          </div>
        </div>
      </div>
      <div>
        <p class="opacity-80 leading-none">
          {{ $date.format(reservation.date.date, $date.presets.long) }}
        </p>
      </div>
      <div class="mt-2">
        <div
          v-for="service in reservation.services"
          :key="`${reservation.idReservation}-${service.idService}`"
          class="flex items-center mb-3 rounded last:mb-0"
        >
          <div
            class="self-stretch w-2 mr-2 rounded"
            :style="{
              backgroundColor: '#' + service.service.borderHexColour
            }"
          />
          <div class="flex-shrink-0 opacity-80 font-semibold mr-4">
            <p data-cy="start-time">
              {{ $date.minutes(service.startTime) }}
            </p>
            <p data-cy="finish-time">
              {{ $date.minutes(service.startTime + service.duration) }}
            </p>
          </div>
          <div class="w-full">
            <p data-cy="title">
              {{ service.service.title }}
            </p>
            <p class="text-sm opacity-80 flex flex-wrap">
              <span class="mr-4">
                {{ $t('datetime.increments.mins', { mins: service.duration }, Number(service.duration)) }}
              </span>
              <span v-if="service.employee && service.employee.displayName" class="mr-4">
                {{ service.employee.displayName }}
              </span>
              <span class="mr-4">
                {{ service.priceLabel }}
              </span>
            </p>
          </div>
        </div>
      </div>
      <div v-if="reservation.groupCustomers" class="mt-4 pt-2 border-t">
        <template v-for="customer in reservation.groupCustomers" :key="customer.idReservationCustomer">
          <div
            v-if="customer.isStatusUnconfirmed"

            class="flex items-center mb-1 rounded last:mb-0"
          >
            <div class="w-full">
              <p data-cy="title">
                {{ customer.customer.displayName }}
              </p>
            </div>
            <o-chip variant="warning" size="sm" flat>
              {{ $t('general.unconfirmed') }}
            </o-chip>
          </div>
        </template>
      </div>
      <div v-if="reservation.notes" class="opacity-80">
        <p v-html="$t('components.dashboard.elements.unconfirmed.note', { notes: reservation.notes })" />
      </div>
    </o-card>
  </o-panel-link>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useNuxtApp } from '#app'

import Status from '@/components/reservation/elements/Status.vue'
import { ServiceCustomerType } from '~/_api/models/service'

const { $i18n } = useNuxtApp()

interface Props {
  reservation: Record<string, any>
  locationTime?: number | null
}

const props = withDefaults(defineProps<Props>(), {
  locationTime: null,
})

const single = computed(() => {
  return props.reservation.customerType !== ServiceCustomerType.group
})

const startDistance = computed(() => {
  const { _startTime } = props.reservation

  if (props.locationTime) {
    return _startTime - props.locationTime
  }

  return null
})

const timeLabel = computed(() => {
  if (startDistance.value && startDistance.value <= 45) {
    if (startDistance.value > 0) {
      return $i18n.t('components.dashboard.elements.inMins', { count: startDistance.value })
    }

    if (props.locationTime! <= props.reservation._finishTime) {
      return $i18n.t('general.now')
    }
  }

  return null
})
</script>
